<template>
  <div class="app">
    <router-view v-if="loaded" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { IProductAuthorization } from '@rready/composables';
import {
  loadApplicationScope,
  checkTermsAndConditions,
  productAuthorizationReport
} from '@rready/composables';
import { AppNavigation } from '@rready/integration';
import type { IApplicationScope } from './store/applicationScopeStore';
import { useAuthStore } from './store/authStore';
import { useApplicationScopeStore } from './store/applicationScopeStore';
import { useContextStore } from './store/contextStore';

export default defineComponent({
  data() {
    return {
      loaded: false
    };
  },
  async created() {
    const userId = useAuthStore().token?.userId;
    const contextId = useContextStore().contextId;
    if (userId && contextId) {
      await this.loadScope();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    async loadScope(): Promise<void> {
      try {
        const userId = useAuthStore().token?.userId;
        const contextId = useContextStore().contextId;

        if (!userId) {
          console.warn('User not logged in');
          return;
        }

        if (!contextId) {
          this.handleNoContextError();
          return;
        }

        // Load application scope
        const scope = await loadApplicationScope(this.$api, contextId, userId);

        // Handle loading errors
        if (scope === 'ERROR_LOADING_USER') {
          this.handleUserLoadingError();
          return;
        }
        if (scope === 'ERROR_LOADING_CONTEXT') {
          this.handleContextLoadingError(contextId);
          return;
        }
        if (scope === 'ERROR_GENERIC') {
          this.handleGenericError();
          return;
        }

        // Check terms and conditions
        const { accepted, current } = checkTermsAndConditions(scope);
        if (accepted < current) {
          this.handleTermsAndConditions(scope);
          return;
        }

        // Check authorization report
        const report = productAuthorizationReport(scope);
        if (!this.hasAvailableProducts(report)) {
          this.handleNoAvailableProductError();
          return;
        }

        // This is 100% duplicated with setupStores, just trying something out
        useApplicationScopeStore().setup(scope);
        this.setupStores(scope, contextId);
      } catch (e: any) {
        this.$sentry.captureException(e);
        this.handleGenericError();
      } finally {
        this.loaded = true;
      }
    },
    setupStores(scope: IApplicationScope, contextId: string) {
      useAuthStore().updateUser(scope.user);
      useAuthStore().updatePermission(scope.roles, scope.actions);
      useContextStore().updateContext(contextId);
      useContextStore().updateOrganization(scope.organization);
    },
    hasAvailableProducts(
      report: Record<string, IProductAuthorization>
    ): boolean {
      return Object.values(report).some((it) => it.enabled && it.granted);
    },
    handleTermsAndConditions(scope: IApplicationScope) {
      const context = scope.organization.context;
      const url = AppNavigation.authTermsUrl(
        context,
        'portal',
        this.$route.fullPath
      );
      window.location.replace(url);
    },
    handleUserLoadingError() {
      this.$router.replace({
        name: 'AppError',
        params: {
          errorCode: 'unknown'
        }
      });
    },
    handleNoContextError() {
      this.$router.replace({
        name: 'AppError',
        params: {
          errorCode: 'unknown'
        }
      });
    },
    handleContextLoadingError(contextId: string) {
      this.$router.replace({
        name: 'AppError',
        params: {
          errorCode: 'invalid-context'
        },
        query: {
          invalidContextId: contextId
        }
      });
    },
    handleGenericError() {
      this.$router.replace({
        name: 'AppError',
        params: {
          errorCode: 'unknown'
        }
      });
    },
    handleNoAvailableProductError() {
      this.$router.replace({
        name: 'AppError',
        params: {
          errorCode: 'unknown'
        }
      });
    }
  }
});
</script>

<style lang="scss">
html {
  overflow-y: scroll;
}
</style>
